import {PersonType} from "types/person-types";
import Button from "components/common/button/Button";
import {faPlus, faTrash} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useTranslation} from "react-i18next";
import Input from "components/common/form/inputs/Input";
import PersonDropdown from "components/entities/app/person/partials/inputs/PersonDropdown";
import If from "../../../common/If";
import HTMLCode from "components/common/HTMLCode";
import Translate from "components/common/Translate";
import {uuid} from "../../../../config/app-constants";
import {removeAt} from "../../../../utilities/app-utilities";
import {useContext, useEffect, useRef, useState} from "react";
import {getFormContext} from "../../../../config/FormContext";
import { PersonFilterInput } from "types/graphql";

export type WorkersFormType = {
	person?: PersonType,
	price?: number | string,
	id: string | number
}

type WorkersInputPropsType = {
	onChange?: (value: WorkersFormType[]) => void,
	value?: WorkersFormType[],
	name?: string
	required?: boolean,
	min?: number
	max?: number,
	isTemplate?: boolean,
	hidePrice?: boolean
}

const WorkersInput = (props: WorkersInputPropsType) => {
	const [t] = useTranslation();
	const hiddenInputRef = useRef<HTMLInputElement>(null);
	const [workerError, setWorkerError] = useState<string>('');
	const formContext = getFormContext(useContext);

	useEffect(() => {
		checkValidity();
	}, [props.value]);

	const personDropdownWhere = () : PersonFilterInput | undefined => {
		if(props.value && props.value.length) {
			return {
				id: {
					nin: props.value.filter(x => Number(x.person?.id)).map(w => Number(w.person!.id))
				}
			}
		}

		return undefined;
	}

	const checkValidity = () => {
		let error = '';
		if (props.required && !props.value?.length) {
			error = 'required';
		}
		if (props.min && props.value?.length && props.value.length < props.min) {
			error = 'min';
		}
		if (props.max && props.value?.length && props.value.length > props.max) {
			error = 'max';
		}
		hiddenInputRef.current?.setCustomValidity(error);
		setWorkerError(error);
	}

	const onChange = (workers: WorkersFormType[]) => {
		if (props.onChange) props.onChange(workers);
		checkValidity();
	};

	const addWorker = () => {
		let value = props.value ? [...props.value] : [];
		value.push(getWorkerObj())
		onChange(value);
	}

	const setWorker = (worker: Partial<WorkersFormType>, index: number) => {
		let value = props.value ? [...props.value] : [];
		value[index] = {
			...value[index],
			...worker
		};

		onChange(value);
	};

	const removeWorker = (index: number) => {
		let value = props.value ? [...props.value] : [];
		value = removeAt(value, index);
		if (props.required && !value.length) {
			value.push(getWorkerObj());
		}

		onChange(value);
	};

	const getWorkerObj = () => {
		return {
			id: uuid(),
			price: '',
			person: undefined
		}
	}

	return (
		<>
            <h2 className={`${workerError && formContext.submitted ? 'text-danger' : ''}`}>Workers</h2>
            <If condition={workerError && formContext.submitted}>
                <span className="text-danger text-sm">
                    <Translate i18nKey={`form.errors.array.` + workerError}/>
                </span>
            </If>
            <hr/>
            <input title="for validation only" ref={hiddenInputRef} className="hidden" />
            {props.value?.map((w, i) => {
                return (
                    <>
                        <div key={`${props.name || ''}-work-form-page-worker${w.id}`}
                            className="flex gap-4 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                        >
                            <PersonDropdown
                                required
								where={personDropdownWhere()}
                                value={w.person}
                                onChange={v => setWorker({person: v}, i)}
                                addPerson
                            />
                            <If condition={!props.hidePrice}>
                                <Input
                                    type="number"
                                    required={!props.isTemplate}
                                    name="price"
                                    label="fields.price.name"
                                    value={w.price?.toString() || ''}
                                    onChange={v => setWorker({price: v}, i)}
                                />
                            </If>
                            <Button title={t('button.remove')} theme="link" className="text-danger mt-4"
                                onClick={() => removeWorker(i)}>
                                <FontAwesomeIcon icon={faTrash}/>
                            </Button>
                        </div>
                        <hr/>
                    </>
                );
            })}
            <If condition={!props.max || (props.value?.length || 0) >= props.max}>
                <Button title={t('button.add')} className="mt-1" onClick={addWorker}>
                    <FontAwesomeIcon icon={faPlus}/>
                    <HTMLCode/>
                    <Translate i18nKey="button.add"/>
                    <HTMLCode/>
                    <Translate i18nKey="fields.workers.name" transform="lowercase"/>
                </Button>
            </If>
		</>
	)
}


export default WorkersInput;
